import { languages } from "@codeshare/languages";
import {
  describeProgress,
  type RunExit,
  type RunHandle,
  type TestResult,
} from "@codeshare/runner-core";
import { LineDiscipline } from "@codeshare/terminal";
import type { TerminalView } from "@codeshare/terminal/view";
import { RUNNERS } from "./runners";

/** A run as it starts (for sharing it with the room). */
export interface RunStart {
  entry: string;
  runnerId: string;
  /** Resolves when it ends. */
  exit: Promise<RunExit>;
}

export type RunStatus =
  | { kind: "idle" }
  | { kind: "loading"; message: string }
  | { kind: "running"; file: string; testing?: boolean }
  | { kind: "exited"; file: string; exit: RunExit; testing?: boolean };

/** The languages that can run here, by name (for saying what can when something can't). */
export function runnableLanguages(): string[] {
  return Object.values(RUNNERS).map((runner) => runner.name);
}

/** Why a file can't run, or undefined if it can. */
export function cannotRun(languageId: string): string | undefined {
  const runner = languages.get(languageId)?.runner;
  const name = languages.get(languageId)?.name ?? "Plain text";
  if (!runner)
    return `${name} can't run in the browser. Runs here: ${runnableLanguages().join(", ")}.`;
  if (!RUNNERS[runner]) return `The ${runner} runner isn't available yet`;
  return undefined;
}

/** A key for a test result: its file and name. */
export const testKey = (test: Pick<TestResult, "file" | "name">) =>
  `${test.file ?? ""}::${test.name}`;

const encoder = new TextEncoder();
const DEFAULT_SIZE = { cols: 80, rows: 24 };
/** Output kept for terminals that attach mid-run. */
const MAX_OUTPUT_BYTES = 1024 * 1024;

/**
 * Runs the open file locally and connects it to a terminal: output in, typing (through a line
 * discipline) out, Ctrl-C to stop. Only the person who clicks Run runs anything.
 */
export class RunSession {
  #terminal: TerminalView | undefined;
  #handle: RunHandle | undefined;
  #status: RunStatus = { kind: "idle" };
  readonly #listeners = new Set<() => void>();
  readonly #runListeners = new Set<(run: RunStart) => void>();
  readonly #writeListeners = new Set<(data: string | Uint8Array) => void>();
  readonly #resizeListeners = new Set<(size: { cols: number; rows: number }) => void>();
  readonly #discipline = new LineDiscipline({
    echo: (text) => this.#write(text),
    submit: (line) => this.#handle?.writeStdin(line),
    interrupt: () => this.stop(),
    eof: () => this.#handle?.closeStdin(),
  });
  #offData: (() => void) | undefined;
  /** The run in progress, if any. */
  #current: RunStart | undefined;
  /** The current run's terminal output, replayed to terminals that attach later. */
  #output: (string | Uint8Array)[] = [];
  #outputBytes = 0;
  #offResize: (() => void) | undefined;
  /** The latest test results (one run of all tests, plus later single-test runs). */
  #tests: readonly TestResult[] = [];
  readonly #testListeners = new Set<() => void>();

  constructor() {
    this.#discipline.enabled = false;
  }

  get status(): RunStatus {
    return this.#status;
  }

  /** The latest test results, as a stable array between changes (for React). */
  get tests(): readonly TestResult[] {
    return this.#tests;
  }

  subscribeTests(listener: () => void): () => void {
    this.#testListeners.add(listener);
    return () => this.#testListeners.delete(listener);
  }

  #setTests(tests: readonly TestResult[]): void {
    this.#tests = tests;
    for (const listener of this.#testListeners) listener();
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** Called as each run starts. */
  onRun(listener: (run: RunStart) => void): () => void {
    this.#runListeners.add(listener);
    return () => this.#runListeners.delete(listener);
  }

  /** Everything shown in the terminal for runs (output, echoed input, notes), as it's written. */
  onWrite(listener: (data: string | Uint8Array) => void): () => void {
    this.#writeListeners.add(listener);
    return () => this.#writeListeners.delete(listener);
  }

  /** The terminal's size (a standard 80×24 while none is on screen). */
  get size(): { cols: number; rows: number } {
    return this.#terminal ? { cols: this.#terminal.cols, rows: this.#terminal.rows } : DEFAULT_SIZE;
  }

  onResize(listener: (size: { cols: number; rows: number }) => void): () => void {
    this.#resizeListeners.add(listener);
    return () => this.#resizeListeners.delete(listener);
  }

  /** The run in progress (undefined once it ends). */
  get current(): RunStart | undefined {
    return this.#current;
  }

  /** Everything shown for the current run so far (capped; the oldest output goes first). */
  get output(): readonly (string | Uint8Array)[] {
    return this.#output;
  }

  /** Input as if typed into this terminal (e.g. from a guest the host lets type). */
  input(data: string): void {
    this.#discipline.input(data);
  }

  attachTerminal(terminal: TerminalView): () => void {
    this.#terminal = terminal;
    for (const data of this.#output) terminal.write(data);
    this.#offData = terminal.onData((data) => this.#discipline.input(data));
    const notify = () => {
      for (const listener of this.#resizeListeners) listener(this.size);
    };
    this.#offResize = terminal.onResize(notify);
    notify();
    return () => {
      this.#offData?.();
      this.#offResize?.();
      if (this.#terminal === terminal) this.#terminal = undefined;
    };
  }

  run(options: {
    entry: string;
    languageId: string;
    files: Record<string, string>;
    args?: string[];
    /** Load npm packages from this CDN (when the user allowed it). */
    packageCdn?: string;
    /** "test": run the tests (all of the entry's, or `test` only). */
    mode?: "run" | "test";
    test?: string;
    /** Which of the runner's toolchains (e.g. the room's OCaml compiler). */
    toolchain?: string;
  }): void {
    const runnerId = languages.get(options.languageId)?.runner;
    const runner = runnerId ? RUNNERS[runnerId] : undefined;
    if (!runner) return;
    this.stop();
    this.#terminal?.clear();
    this.#output = [];
    this.#outputBytes = 0;
    const testing = options.mode === "test";
    const handle = runner.run({
      files: options.files,
      entry: options.entry,
      args: options.args,
      ...(options.packageCdn ? { packageCdn: options.packageCdn } : {}),
      ...(options.toolchain ? { toolchain: options.toolchain } : {}),
      ...(testing
        ? { mode: "test" as const, ...(options.test ? { test: options.test } : {}) }
        : {}),
    });
    if (testing) {
      // All tests: a fresh set of results. One test: replace just its result.
      this.#setTests(
        options.test
          ? this.#tests.filter(
              (t) => t.name !== options.test && t.name.split(/::|#| > /).at(-1) !== options.test,
            )
          : [],
      );
      handle.onTest((result) => {
        if (this.#handle !== handle) return;
        const key = testKey(result);
        this.#setTests([...this.#tests.filter((t) => testKey(t) !== key), result]);
      });
    }
    this.#handle = handle;
    const start: RunStart = { entry: options.entry, runnerId: runner.id, exit: handle.exit };
    this.#current = start;
    void handle.exit.then(() => {
      if (this.#current === start) this.#current = undefined;
    });
    for (const listener of this.#runListeners) listener(start);
    const command = testing
      ? [options.entry, ...(options.test ? [options.test] : [])].map(quoteArg).join(" ")
      : [options.entry, ...(options.args ?? []).map(quoteArg)].join(" ");
    this.#write(`\x1b[2m$ ${testing ? "test" : "run"} ${command}\x1b[0m\r\n`);
    this.#set({ kind: "running", file: options.entry, testing });
    if (!handle.stdinSupported) {
      this.#write(
        "\x1b[33m(input isn't available on this page: it isn't cross-origin isolated)\x1b[0m\r\n",
      );
    }
    this.#discipline.enabled = true;
    handle.onProgress((progress) => {
      if (this.#handle === handle)
        this.#set({ kind: "loading", message: describeProgress(progress) });
    });
    handle.onStatus((message) => {
      if (this.#handle !== handle) return;
      this.#set(
        message === null
          ? { kind: "running", file: options.entry, testing }
          : { kind: "loading", message },
      );
    });
    handle.onOutput((stream, data) => {
      if (this.#handle !== handle) return;
      if (this.#status.kind === "loading")
        this.#set({ kind: "running", file: options.entry, testing });
      this.#write(stream === "stderr" ? colorize(data) : data);
    });
    void handle.exit.then((exit) => {
      if (this.#handle !== handle) return;
      this.#discipline.enabled = false;
      this.#handle = undefined;
      const note =
        exit.reason === "killed"
          ? "stopped"
          : exit.reason === "timeout"
            ? "timed out"
            : exit.reason === "error"
              ? `runtime error: ${exit.message}`
              : `exited with code ${exit.code}`;
      this.#write(`\r\n\x1b[2m[${note}]\x1b[0m\r\n`);
      this.#set({ kind: "exited", file: options.entry, exit, testing });
    });
  }

  stop(): void {
    this.#handle?.kill();
  }

  #write(data: string | Uint8Array): void {
    this.#terminal?.write(data);
    this.#output.push(data);
    this.#outputBytes += typeof data === "string" ? data.length : data.byteLength;
    while (this.#outputBytes > MAX_OUTPUT_BYTES && this.#output.length > 1) {
      const dropped = this.#output.shift() as string | Uint8Array;
      this.#outputBytes -= typeof dropped === "string" ? dropped.length : dropped.byteLength;
    }
    for (const listener of this.#writeListeners) listener(data);
  }

  #set(status: RunStatus): void {
    this.#status = status;
    for (const listener of this.#listeners) listener();
  }
}

/** An argument as it could be typed back (quoted when needed). */
function quoteArg(arg: string): string {
  return /^[\w@%+=:,./-]+$/.test(arg) ? arg : `'${arg.replaceAll("'", `'\\''`)}'`;
}

/** stderr in red. */
function colorize(data: Uint8Array): Uint8Array {
  const prefix = encoder.encode("\x1b[31m");
  const suffix = encoder.encode("\x1b[0m");
  const out = new Uint8Array(prefix.length + data.length + suffix.length);
  out.set(prefix);
  out.set(data, prefix.length);
  out.set(suffix, prefix.length + data.length);
  return out;
}
